/**
 * Dynamic OpenLayers Bridge
 * 
 * Loads either ol3-bridge or ol10-bridge based on settings.json configuration.
 * All application code should import from this file, not the versioned bridges directly.
 * 
 * Configuration:
 *   - settings.json: { "openLayers": { "version": "3" | "10" } }
 *   - Default: "3" (OpenLayers 3.15 from lib/ol/ol.js)
 * 
 * Usage:
 *   import { Map, View, TileLayer } from './openlayers/ol-bridge';
 *   const map = new Map({ ... });
 * 
 * Benefits:
 *   - Single import point for all OpenLayers usage
 *   - Seamless switching between OL3 and OL10
 *   - Type-safe (TypeScript checks both bridges)
 *   - Zero code changes needed when switching versions
 * 
 * Note: The version selection happens at webpack build time by reading settings.json.
 * This file is conditionally loaded based on webpack's DefinePlugin configuration.
 */

// Import settings at build time (webpack will inline this)
// @ts-ignore - webpack will resolve this at build time
import * as settingsJson from '../../../settings.json';

// Determine which version to use from settings.json
const settings: any = settingsJson;
const USE_OPENLAYERS_10 = 
    settings?.featureFlags?.useOpenLayers10 === true || 
    settings?.openLayers?.version === '10' ||
    false;

// Import OL3 CSS when using OL10 to preserve legacy styling expectations.
// OL3 CSS is also imported in ol3-bridge.ts for OL3 builds.
if (USE_OPENLAYERS_10) {
    // @ts-ignore
    void import('ol/ol.css');
}

if (USE_OPENLAYERS_10) {
    console.log('[ol-bridge] Using OpenLayers 10 (from settings.json)');
} else {
    console.log('[ol-bridge] Using OpenLayers 3 (from settings.json)');
}

// Re-export everything from the appropriate bridge
// TypeScript requires explicit exports, so we import from both and conditionally select
import * as ol3 from './ol3-bridge';
import * as ol10 from './ol10-bridge';

// Select the appropriate bridge based on configuration
const bridge = USE_OPENLAYERS_10 ? ol10 : ol3;

if (USE_OPENLAYERS_10) {
    console.log('[ol-bridge] Loading OpenLayers 10 bridge');
} else {
    console.log('[ol-bridge] Loading OpenLayers 3 bridge');
}

// Core exports
export const Map = bridge.Map;
export const View = bridge.View;
export const Feature = bridge.Feature;
export const Overlay = bridge.Overlay;
export const Collection = bridge.Collection;

// Layer exports
export const TileLayer = bridge.TileLayer;
export const VectorLayer = bridge.VectorLayer;
export const layer = bridge.layer;

// Source exports
export const XYZ = bridge.XYZ;
export const VectorSource = bridge.VectorSource;
export const ClusterSource = bridge.ClusterSource;
export const TileGrid = bridge.TileGrid;
export const source = bridge.source;
export const tilegrid = bridge.tilegrid;

// Geometry exports
export const GeomPoint = bridge.GeomPoint;
export const Polygon = bridge.Polygon;
export const LineString = bridge.LineString;
export const geom = bridge.geom;

// Interaction exports
export const Modify = bridge.Modify;
export const interaction = bridge.interaction;

// Control exports
export const Control = bridge.Control;
export const MousePosition = bridge.MousePosition;
export const control = bridge.control;

// Projection & coordinate exports
export const Projection = bridge.Projection;
export const proj = bridge.proj;
export const coordinate = bridge.coordinate;
export const extent = bridge.extent;

// Style exports
export const Style = bridge.Style;
export const Icon = bridge.Icon;
export const Fill = bridge.Fill;
export const Stroke = bridge.Stroke;
export const Text = bridge.Text;
export const Circle = bridge.Circle;
export const style = bridge.style;

// Format exports
export const GeoJSON = bridge.GeoJSON;
export const format = bridge.format;

// Inheritance helper (deprecated in OL10)
export const inherits = bridge.inherits;

// Export the version being used for runtime checks
export const OPENLAYERS_VERSION = USE_OPENLAYERS_10 ? '10' : '3';
export const IS_OPENLAYERS_10 = USE_OPENLAYERS_10;

/**
 * Get OpenLayers version information
 */
export function getOpenLayersInfo() {
    return {
        version: OPENLAYERS_VERSION,
        isOL10: IS_OPENLAYERS_10,
        source: IS_OPENLAYERS_10 ? 'npm (ol@10.7.0)' : 'CDN (lib/ol/ol.js v3.15)'
    };
}
